import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import chatService from '../services/chat.service';
import { Conversation, Message } from '../types/MessageTypes';
import { timeAgo } from '../utils/formatters';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

interface MessagesProps {
  darkMode: boolean;
}

const Messages: React.FC<MessagesProps> = ({ darkMode }) => {
  const { isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvo, setActiveConvo] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isAuthenticated) loadConversations();
    else setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    setLoading(true);
    const convos = await chatService.getConversations();
    setConversations(convos);
    setLoading(false);
  };

  const selectConversation = async (referralId: string) => {
    setActiveConvo(referralId);
    const msgs = await chatService.getMessages(referralId);
    setMessages(msgs);
    await chatService.markAsRead(referralId);
    setConversations(prev =>
      prev.map(c => c.referral_id === referralId ? { ...c, unread_count: 0 } : c)
    );
  };

  const handleSend = async () => {
    if (!input.trim() || sending || !activeConvo) return;
    setSending(true);
    try {
      const msg = await chatService.sendMessage(activeConvo, input.trim());
      setMessages(prev => [...prev, msg]);
      setInput('');
      setConversations(prev =>
        prev.map(c =>
          c.referral_id === activeConvo
            ? { ...c, last_message: msg.content, last_message_at: msg.created_at }
            : c
        )
      );
    } catch {
      // silently fail
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <div className="empty-state">
          <h3>Sign in to view your messages</h3>
          <p>Message posters and referrers about specific referrals.</p>
        </div>
      </div>
    );
  }

  const activeConversation = conversations.find(c => c.referral_id === activeConvo);

  return (
    <div className={`page-container messages-page ${darkMode ? 'dark' : ''}`}>
      <h1>Messages</h1>
      <p className="page-subtitle">Communicate about your referrals.</p>

      {loading ? (
        <div className="loading-state">Loading messages...</div>
      ) : conversations.length === 0 ? (
        <div className="empty-state">
          <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'var(--text-secondary)' }} />
          <h3>No conversations yet</h3>
          <p>When you send or receive messages about referrals, they will appear here.</p>
        </div>
      ) : (
        <div className="messages-layout">
          <div className={`messages-sidebar ${activeConvo ? 'hide-mobile' : ''}`}>
            {conversations.map(convo => (
              <div
                key={convo.referral_id}
                className={`conversation-item ${activeConvo === convo.referral_id ? 'active' : ''}`}
                onClick={() => selectConversation(convo.referral_id)}
              >
                <div className="conversation-info">
                  <h4>{convo.candidate_name}</h4>
                  <p className="conversation-vacancy">{convo.vacancy_title}</p>
                  <p className="conversation-preview">{convo.last_message}</p>
                </div>
                <div className="conversation-meta">
                  <span className="conversation-time">{timeAgo(convo.last_message_at)}</span>
                  {convo.unread_count > 0 && (
                    <span className="unread-badge">{convo.unread_count}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={`messages-thread ${!activeConvo ? 'hide-mobile' : ''}`}>
            {!activeConvo ? (
              <div className="messages-thread-empty">
                <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'var(--text-secondary)' }} />
                <p>Select a conversation to start messaging</p>
              </div>
            ) : (
              <>
                <div className="messages-thread-header">
                  <button className="messages-back-btn" onClick={() => setActiveConvo(null)}>
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div>
                    <h3>{activeConversation?.candidate_name}</h3>
                    <p>{activeConversation?.vacancy_title}</p>
                  </div>
                </div>

                <div className="messages-thread-body">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`chat-message ${msg.sender_id === 'mock-user' ? 'sent' : 'received'}`}
                    >
                      <div className="chat-message-content">{msg.content}</div>
                      <div className="chat-message-meta">
                        <span className="chat-message-sender">{msg.sender_name}</span>
                        <span className="chat-message-time">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="messages-thread-input">
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    disabled={sending}
                  />
                  <button onClick={handleSend} disabled={!input.trim() || sending}>
                    <SendIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
